import { Currency, Page } from '@/types/Page'
import { Domain } from '../domain-config/domain-config'
import { toUtfBase64String } from '@/lib/utils/utils'
import { getConfiguration } from '@/lib/utilsUsableOnEdgeRuntime'
import {
  AgencyData,
  HotelPageData,
  HotelsSiteMap,
  PagesSiteMap,
  SiteMapData,
  SiteMapType,
} from '@/types/Misc'
import { API_URL, PageType, SKIP_CACHE_WARMING } from '@/lib/sharedConstants'
import { Payment } from '@mollie/api-client'

const API_CREDENTIALS_USER = process.env.API_CREDENTIALS_USER || ''
const API_CREDENTIALS_PW = process.env.API_CREDENTIALS_PW || ''

const ONCE_A_DAY = 60 * 60 * 24

export async function fetchPageData(
  domain: string,
  slug: string[],
  currency: Currency,
  pageType: PageType,
): Promise<Page> {
  const slugString = slug === undefined ? `/` : `/${slug.join('/')}/`

  return fetchData<Page>(
    `${API_URL}pages/page/?domain=${domain}&slug=${slugString}&currency=${currency}&sem=${
      pageType == PageType.SEM ? '1' : ''
    }`,
    { tags: [slugString, 'ALL'] },
  )
}

export async function fetchCopyrightData(): Promise<string[]> {
  return fetchData<string[]>(`${API_URL}pages/copyrights/`, {
    revalidate: ONCE_A_DAY,
    tags: ['/copyright/'],
  })
}

export async function fetchCacheWarmerData(
  ignoreSkipCacheWarming?: boolean,
): Promise<CacheWarmerParams[]> {
  if (SKIP_CACHE_WARMING && !ignoreSkipCacheWarming) {
    return []
  }

  const cacheWarmerItems = await fetchData<CacheWarmerItem[]>(`${API_URL}pages/cachewarmer/`, {
    revalidate: 0,
  })

  return await Promise.all(
    Object.values(cacheWarmerItems).flatMap((item) =>
      Object.values(item.Slugs).map(async (slug) => ({
        Slug: slug.split('/').filter(Boolean),
        Domain: item.Domain,
        Currency: (await getConfiguration(item.Domain)).fallbackCurrency,
      })),
    ),
  )
}

export async function updatePaymentMethod(domain: Domain, args: Payment) {
  return await fetch(`${API_URL}payment/mollie/?domain=${domain}`, {
    method: 'POST',
    headers: {
      Authorization: getAuthorizationString(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(args),
  }).then((response) => {
    if (!response.ok) {
      throw new Error('Failed to update payment method')
    }

    return response
  })
}

function getAuthorizationString(): string {
  return 'Basic ' + toUtfBase64String(API_CREDENTIALS_USER + ':' + API_CREDENTIALS_PW)
}

async function callAPI(
  url: string,
  nextConfig: NextFetchRequestConfig | undefined,
): Promise<Response> {
  return await fetch(url, {
    method: 'GET',
    headers: {
      Authorization: getAuthorizationString(),
    },
    next: nextConfig,
  })
}

export async function fetchData<T>(url: string, nextConfig?: NextFetchRequestConfig): Promise<T> {
  const result = await callAPI(url, nextConfig)

  return (await result.json()) as T
}

export interface CacheWarmerItem {
  Domain: Domain
  Slugs: string[]
}

export interface CacheWarmerParams {
  Domain: Domain
  Slug: string[]
  Currency: string
}

export interface ExtendedHotelInfo extends BasicHotelInfo {
  PriceString: number
  SpecialCategory?: string
  Annotation: string
  PriceLine1: string
  PriceLine2: string
  PriceOriginal: number
  RatingValue: number
  Price: number
  Discount: string
}

export interface BasicHotelInfo {
  HotelCode: string
  HotelName: string
  Latitude: number
  Longitude: number
  Images: string[]
  Url: string
  Destinations: string[]
  HotelCategoryStars: number
  HotelCategoryText: string
  RatingText: string
  RatingValue: number
}

function fixPropertyNames(data: Record<string, string | string[]>[]): SiteMapData[] {
  if (data[0].domain) {
    return data as unknown as SiteMapData[]
  }

  return data.map((d) => {
    return {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      domain: d.Domain,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      slugs: d.Slugs,
    }
  }) as SiteMapData[]
}

export async function fetchSiteMapData(type: SiteMapType): Promise<SiteMapData[]> {
  const res = await fetchData<Record<string, string | string[]>[]>(`${API_URL}${type}/`, {
    revalidate: 0,
  })
  return fixPropertyNames(res)
}

export async function fetchHotelSiteMapData(domain: Domain): Promise<HotelsSiteMap | undefined> {
  const res = await fetchData<SiteMapData[]>(`${API_URL}hotels/`, {
    revalidate: ONCE_A_DAY,
  })
  const filteredResult = res.find((siteMapData) => siteMapData.domain === domain)
  return filteredResult?.Sitemap as HotelsSiteMap
}

export async function fetchPagesSiteMapData(domain: Domain): Promise<PagesSiteMap | undefined> {
  const res = await fetchData<SiteMapData[]>(`${API_URL}pages/`, {
    revalidate: ONCE_A_DAY,
    tags: ['ALL'],
  })
  const filteredResult = res.find((siteMapData) => siteMapData.Domain === domain)
  return filteredResult?.Sitemap as PagesSiteMap
}

export async function fetchAgencyData(domain: Domain, agencyNumber: number): Promise<AgencyData> {
  return await fetchData<AgencyData>(
    `${API_URL}agencies/agency/?domain=${domain}&agencynumber=${agencyNumber}`,
    {
      revalidate: ONCE_A_DAY,
      tags: ['ALL'],
    },
  )
}

function protocol(): string {
  return process.env.NODE_ENV === 'production' ? 'https://' : 'http://'
}

export function getSiteMapHost(host: string): string {
  return `${protocol()}${host}`
}

export async function fetchHotelData(
  domain: string,
  slug: string,
  currency: Currency,
): Promise<HotelPageData> {
  const hotelSlug = `/hotel/${slug}`
  const hotelCode = slug.split('-').pop()?.replace('/', '') || ''
  const params = new URLSearchParams({ domain, currency, slug: hotelSlug })

  return await fetchData<HotelPageData>(`${API_URL}hotels/hotel?${params.toString()}`, {
    tags: [hotelCode, 'ALL'],
  })
}
